/**
 * The preferences window's navigation tree (见 21): the platform frame
 * (`PLATFORM_SETTING_*`) merged with every module's `contributes.setting`.
 *
 * Rules, all applied here so the window only draws the result:
 * - order ascending, ties by owner module id, then by id;
 * - a page whose group, or a section whose page, is not present is dropped
 *   (the extension host already logged the dangling reference);
 * - a page under `platform.apps` needs a non-custom activityBar item from its
 *   module, so an app nobody can launch does not get a settings page;
 * - a page with no section, and a group with no page, is not shown;
 * - Developer options appear only once unlocked.
 */
import {
  type IconSpec,
  type ModuleSettingActionConfirm,
  PLATFORM_MODULE_ID,
  PLATFORM_SETTING_GROUPS,
  PLATFORM_SETTING_KEY_SECTIONS,
  PLATFORM_SETTING_PAGES,
  type PlatformSettingBuiltin,
  type ResolvedActivityBarItem,
  type ResolvedModuleSetting,
  SETTING_GROUP_APPS,
  SETTING_PAGE_DEVELOPER,
} from '@hibro/shared';

export type SettingsSectionBody =
  | {
      readonly kind: 'view';
      readonly moduleId: string;
      readonly viewId: string;
    }
  | {
      readonly kind: 'keys';
      readonly moduleId: string;
      readonly keys: readonly string[];
    }
  | { readonly kind: 'builtin'; readonly builtin: PlatformSettingBuiltin };

export interface SettingsTreeSection {
  /** Global id; also the `data-settings-anchor` value. */
  readonly id: string;
  readonly moduleId: string;
  readonly title?: string;
  readonly body: SettingsSectionBody;
}

export interface SettingsTreePage {
  readonly id: string;
  readonly moduleId: string;
  readonly title: string;
  readonly icon?: IconSpec;
  readonly sections: readonly SettingsTreeSection[];
  /** Titled sections listed as sidebar scroll anchors; empty below two. */
  readonly anchors: readonly SettingsTreeSection[];
}

export interface SettingsTreeAction {
  readonly id: string;
  readonly moduleId: string;
  readonly title: string;
  readonly icon?: IconSpec;
  readonly command: string;
  readonly danger: boolean;
  readonly closesWindow: boolean;
  /** Already resolved. Present when the row must be confirmed first. */
  readonly confirm?: ModuleSettingActionConfirm;
}

export interface SettingsTreeGroup {
  readonly id: string;
  /** Absent draws the group without a header (the personal group). */
  readonly title?: string;
  readonly pages: readonly SettingsTreePage[];
  /** Drawn after the pages, below a divider. */
  readonly actions: readonly SettingsTreeAction[];
}

export interface SettingsTreeHeader {
  readonly moduleId: string;
  readonly viewId: string;
}

export interface SettingsTree {
  readonly groups: readonly SettingsTreeGroup[];
  readonly header: SettingsTreeHeader | null;
  /** Why no header was chosen although modules declared one; for a warn log. */
  readonly headerProblem?: string;
}

export interface BuildSettingsTreeInput {
  readonly setting: readonly ResolvedModuleSetting[];
  readonly activityBar: readonly Pick<
    ResolvedActivityBarItem,
    'moduleId' | 'custom'
  >[];
  readonly developerEnabled: boolean;
  /** `workbench.settings.headerProvider`; empty when unpinned. */
  readonly headerProvider: string;
  /** Platform message key → text. */
  readonly platformLabel: (key: string) => string;
  /** A module's `%key%` or plain title → text. */
  readonly moduleLabel: (moduleId: string, raw: string) => string;
}

interface Ordered {
  readonly id: string;
  readonly moduleId: string;
  readonly order: number;
}

function byOrder(left: Ordered, right: Ordered): number {
  if (left.order !== right.order) {
    return left.order - right.order;
  }
  if (left.moduleId !== right.moduleId) {
    return left.moduleId < right.moduleId ? -1 : 1;
  }
  if (left.id === right.id) {
    return 0;
  }
  return left.id < right.id ? -1 : 1;
}

interface DraftGroup extends Ordered {
  readonly title?: string;
}
interface DraftPage extends Ordered {
  readonly group: string;
  readonly title: string;
  readonly icon?: IconSpec;
}
interface DraftSection extends Ordered {
  readonly page: string;
  readonly title?: string;
  readonly body: SettingsSectionBody;
}
interface DraftAction extends Ordered, SettingsTreeAction {
  readonly group: string;
}

function pickHeader(
  setting: readonly ResolvedModuleSetting[],
  provider: string,
): Pick<SettingsTree, 'header' | 'headerProblem'> {
  const declared = setting.filter((entry) => entry.header);
  const pinned = provider
    ? declared.find((entry) => entry.moduleId === provider)
    : undefined;
  if (pinned?.header) {
    return {
      header: { moduleId: pinned.moduleId, viewId: pinned.header.viewId },
    };
  }
  if (provider) {
    return {
      header: null,
      headerProblem: `workbench.settings.headerProvider "${provider}" declares no contributes.setting.header`,
    };
  }
  const [only, ...rest] = declared;
  if (!only?.header) {
    return { header: null };
  }
  if (rest.length > 0) {
    return {
      header: null,
      headerProblem: `several modules declare contributes.setting.header (${declared
        .map((entry) => entry.moduleId)
        .join(', ')}); pin one with workbench.settings.headerProvider`,
    };
  }
  return { header: { moduleId: only.moduleId, viewId: only.header.viewId } };
}

export function buildSettingsTree(input: BuildSettingsTreeInput): SettingsTree {
  const { setting, platformLabel, moduleLabel } = input;
  const launched = new Set(
    input.activityBar
      .filter((item) => item.custom !== true)
      .map((item) => item.moduleId),
  );

  const groups: DraftGroup[] = PLATFORM_SETTING_GROUPS.map((group) => ({
    id: group.id,
    moduleId: PLATFORM_MODULE_ID,
    order: group.order,
    ...(group.titleKey ? { title: platformLabel(group.titleKey) } : {}),
  }));
  const pages: DraftPage[] = [];
  const sections: DraftSection[] = [];
  for (const page of PLATFORM_SETTING_PAGES) {
    if (page.id === SETTING_PAGE_DEVELOPER && !input.developerEnabled) {
      continue;
    }
    pages.push({
      id: page.id,
      moduleId: PLATFORM_MODULE_ID,
      group: page.group,
      title: platformLabel(page.titleKey),
      icon: page.icon,
      order: page.order,
    });
    if (page.builtin) {
      sections.push({
        id: page.id,
        moduleId: PLATFORM_MODULE_ID,
        page: page.id,
        order: 0,
        body: { kind: 'builtin', builtin: page.builtin },
      });
    }
  }
  for (const section of PLATFORM_SETTING_KEY_SECTIONS) {
    sections.push({
      id: section.id,
      moduleId: PLATFORM_MODULE_ID,
      page: section.page,
      order: section.order,
      ...(section.titleKey ? { title: platformLabel(section.titleKey) } : {}),
      body: { kind: 'keys', moduleId: PLATFORM_MODULE_ID, keys: section.keys },
    });
  }

  const actions: DraftAction[] = [];
  for (const entry of setting) {
    const { moduleId } = entry;
    for (const group of entry.groups) {
      groups.push({
        id: group.id,
        moduleId,
        order: group.order,
        title: moduleLabel(moduleId, group.title),
      });
    }
    for (const page of entry.pages) {
      if (page.group === SETTING_GROUP_APPS && !launched.has(moduleId)) {
        continue;
      }
      pages.push({
        id: page.id,
        moduleId,
        group: page.group,
        title: moduleLabel(moduleId, page.title),
        ...(page.icon ? { icon: page.icon } : {}),
        order: page.order,
      });
    }
    for (const section of entry.sections) {
      const body: SettingsSectionBody | null = section.viewId
        ? { kind: 'view', moduleId, viewId: section.viewId }
        : section.keys
          ? { kind: 'keys', moduleId, keys: section.keys }
          : null;
      if (!body) {
        continue;
      }
      sections.push({
        id: section.id,
        moduleId,
        page: section.page,
        order: section.order,
        ...(section.title
          ? { title: moduleLabel(moduleId, section.title) }
          : {}),
        body,
      });
    }
    for (const action of entry.actions) {
      actions.push({
        id: action.id,
        moduleId,
        group: action.group,
        order: action.order,
        title: moduleLabel(moduleId, action.title),
        ...(action.icon ? { icon: action.icon } : {}),
        command: action.command,
        danger: action.danger,
        closesWindow: action.closesWindow,
        ...(action.confirm
          ? {
              confirm: {
                title: moduleLabel(moduleId, action.confirm.title),
                body: moduleLabel(moduleId, action.confirm.body),
                confirm: moduleLabel(moduleId, action.confirm.confirm),
              },
            }
          : {}),
      });
    }
  }

  const sectionsByPage = new Map<string, DraftSection[]>();
  for (const section of [...sections].sort(byOrder)) {
    const list = sectionsByPage.get(section.page) ?? [];
    list.push(section);
    sectionsByPage.set(section.page, list);
  }

  const tree: SettingsTreeGroup[] = [];
  for (const group of [...groups].sort(byOrder)) {
    const groupPages: SettingsTreePage[] = [];
    for (const page of pages
      .filter((item) => item.group === group.id)
      .sort(byOrder)) {
      const pageSections: SettingsTreeSection[] = (
        sectionsByPage.get(page.id) ?? []
      ).map((section) => ({
        id: section.id,
        moduleId: section.moduleId,
        ...(section.title ? { title: section.title } : {}),
        body: section.body,
      }));
      if (pageSections.length === 0) {
        continue;
      }
      const titled = pageSections.filter((section) => section.title);
      groupPages.push({
        id: page.id,
        moduleId: page.moduleId,
        title: page.title,
        ...(page.icon ? { icon: page.icon } : {}),
        sections: pageSections,
        anchors: titled.length >= 2 ? titled : [],
      });
    }
    if (groupPages.length === 0) {
      continue;
    }
    tree.push({
      id: group.id,
      ...(group.title ? { title: group.title } : {}),
      pages: groupPages,
      actions: actions
        .filter((action) => action.group === group.id)
        .sort(byOrder)
        .map(({ group: _group, order: _order, ...action }) => action),
    });
  }

  return { groups: tree, ...pickHeader(setting, input.headerProvider) };
}

export function findSettingsPage(
  tree: SettingsTree,
  pageId: string,
): SettingsTreePage | null {
  for (const group of tree.groups) {
    const page = group.pages.find((item) => item.id === pageId);
    if (page) {
      return page;
    }
  }
  return null;
}

export function firstSettingsPage(tree: SettingsTree): SettingsTreePage | null {
  return tree.groups[0]?.pages[0] ?? null;
}

/** Page hosting a section id; `null` when no visible page has it. */
export function settingsPageOfSection(
  tree: SettingsTree,
  sectionId: string,
): SettingsTreePage | null {
  for (const group of tree.groups) {
    for (const page of group.pages) {
      if (page.sections.some((section) => section.id === sectionId)) {
        return page;
      }
    }
  }
  return null;
}

/**
 * Narrow the tree to the query. A group title hit keeps the whole group; a page
 * is kept when its title, a section title, or a declared key's title matches.
 * Actions match on their own title.
 */
export function filterSettingsTree(
  tree: SettingsTree,
  query: string,
  keyLabel: (moduleId: string, key: string) => string | undefined,
): SettingsTree {
  const needle = query.trim().toLocaleLowerCase();
  if (!needle) {
    return tree;
  }
  const hit = (text: string | undefined) =>
    text !== undefined && text.toLocaleLowerCase().includes(needle);
  const sectionHit = ({ title, body }: SettingsTreeSection) =>
    hit(title) ||
    (body.kind === 'keys' &&
      body.keys.some((key) => hit(keyLabel(body.moduleId, key))));
  const pageHit = (page: SettingsTreePage) =>
    hit(page.title) || page.sections.some(sectionHit);
  const groups: SettingsTreeGroup[] = [];
  for (const group of tree.groups) {
    if (hit(group.title)) {
      groups.push(group);
      continue;
    }
    const pages = group.pages.filter(pageHit);
    const actions = group.actions.filter((action) => hit(action.title));
    if (pages.length === 0 && actions.length === 0) {
      continue;
    }
    groups.push({ ...group, pages, actions });
  }
  return { ...tree, groups };
}
